import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Mic,
  MicOff,
  Send,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Check,
  ArrowRight,
  User,
  Mail,
  Sparkles,
  MessageCircle,
  CheckCircle,
  Circle,
  Clock,
  Shield,
  Heart,
  Zap,
  Eye,
  Battery,
  Target,
  UserCheck,
  Flag,
  ChevronDown,
  MessageSquare,
} from "lucide-react";

import Results from "./components/Results";
import SuccessPage from "./components/SuccessPage";

import { loadMetaPixel } from "./pixel";

import "./App.css";

// --- Meta helpers ---
const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith(name + "="))
    ?.split("=")[1];

// Safer cookie attributes
const isHttps = () =>
  typeof window !== "undefined" &&
  window.location &&
  window.location.protocol === "https:";

const cookieAttrs = () =>
  `path=/; max-age=${90 * 24 * 60 * 60}; SameSite=Lax${
    isHttps() ? "; Secure" : ""
  }`;

function getMetaBrowserIds() {
  let fbp = getCookie("_fbp") || null;
  let fbc = getCookie("_fbc") || null;

  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid");

  if (fbclid) {
    const ts = Math.floor(Date.now() / 1000);
    fbc = `fb.1.${ts}.${fbclid}`;
    document.cookie = `_fbc=${fbc}; ${cookieAttrs()}`;
  }

  if (!fbp) {
    const ts = Math.floor(Date.now() / 1000);
    const rand = Math.random().toString(36).slice(2);
    fbp = `fb.1.${ts}.${rand}`;
    document.cookie = `_fbp=${fbp}; ${cookieAttrs()}`;
  }

  return { fbp, fbc };
}

// NEW: event id generator for de-duplication
const genEventID = () => {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, "0")).join("");
};

// Modern CSS with animations - UPDATED for mobile optimization
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  /* Custom font for titles and questions */
  @font-face {
    font-family: 'P22MackinacPro';
    src: url('/P22MackinacPro-Medium_26.otf') format('opentype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  
  /* Mobile-first optimizations */
  @media (max-width: 640px) {
    .mobile-compact {
      padding: 1rem !important;
    }
    
    .mobile-text-sm {
      font-size: 0.875rem !important;
      line-height: 1.25rem !important;
    }
    
    .mobile-h1 {
      font-size: 1.875rem !important;
      line-height: 2.25rem !important;
    }
    
    .mobile-h2 {
      font-size: 1.5rem !important;
      line-height: 2rem !important;
    }
    
    .mobile-h3 {
      font-size: 1.25rem !important;
      line-height: 1.75rem !important;
    }
  }
  
  /* Loading screen animations */
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(200%); }
  }
  
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
  
  .animation-delay-200 {
    animation-delay: 200ms;
  }
  
  .animation-delay-400 {
    animation-delay: 400ms;
  }
  
  /* Subtle background pattern for depth */
  .hero-gradient-bg {
    background: linear-gradient(135deg, rgba(147, 51, 234, 0.03) 0%, rgba(236, 72, 153, 0.03) 100%);
    position: relative;
  }
  
  .hero-gradient-bg::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 50%, rgba(147, 51, 234, 0.05) 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  /* Mini report preview styling */
  .report-preview {
    position: relative;
    overflow: hidden;
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }
  
  .report-preview::before {
    content: 'Your personalized mini report';
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #7c3aed;
    background: rgba(255, 255, 255, 0.9);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    z-index: 2;
  }
`;

// Modern styled components
const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Smooth scroll to top utility
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

// Categories configuration
const categories = [
  {
    id: "hidden-signal",
    name: "Hidden Signal",
    shortName: "Hidden Signal",
    icon: Eye,
    color: "purple",
    startIndex: 0,
    endIndex: 2,
    description: "Reveal the truths you've been avoiding.",
  },
];

// Questions optimized for Corporate Escapist ICP - Aligned with feedback document
const questionsConfig = [
  {
    id: 1,
    categoryId: "hidden-signal",
    subtitleAbove: "Let's start with your strengths.",
    question:
      "What do friends and colleagues often come to you for — the thing that feels easy for you but hard for them?",
    descriptionBelow: "Also describe how this makes you feel.",
  },
  {
    id: 2,
    categoryId: "hidden-signal",
    subtitleAbove: "Next, your energy patterns.",
    question:
      "Thinking back on your career, what moments bring you the most fulfillment — and which drain you the most?",
    descriptionBelow: "Describe why these moments stand out.",
  },
  {
    id: 3,
    categoryId: "hidden-signal",
    subtitleAbove: "Finally, your future and blockers.",
    question:
      "If you could design your career with no fear or limits, what would it look like — and what’s been holding you back from starting?",
    descriptionBelow: "Be honest and as specific.",
  },
];

// Enhanced Voice Visualizer Component - Optimized for mobile
const VoiceVisualizer = ({ isRecording, stream }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    if (!isRecording || !stream) return;

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    source.connect(analyser);
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.85;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      if (!isRecording) return;

      animationRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // Clear with fade effect
      ctx.fillStyle = "rgba(249, 250, 251, 0.95)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 3.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = (dataArray[i] / 255) * canvas.height * 0.7;

        // Modern gradient bars
        const gradient = ctx.createLinearGradient(
          0,
          canvas.height - barHeight,
          0,
          canvas.height
        );

        const hue = (i / bufferLength) * 60 + 270; // Purple to pink gradient
        gradient.addColorStop(0, `hsla(${hue}, 70%, 60%, 0.8)`);
        gradient.addColorStop(1, `hsla(${hue}, 70%, 50%, 1)`);

        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 2, barHeight);

        x += barWidth;
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContext.state !== "closed") {
        audioContext.close();
      }
    };
  }, [isRecording, stream]);

  return (
    <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 p-0.5">
      <canvas
        ref={canvasRef}
        width={600}
        height={60}
        className="w-full h-14 rounded-xl"
      />
    </div>
  );
};

// Voice Recording Button Component - Mobile optimized
const VoiceRecordingButton = ({
  isRecording,
  isTranscribing,
  audioStream,
  onStartRecording,
  onStopRecording,
  buttonText = "Record Answer",
  buttonSize = "md",
}) => {
  const buttonClasses =
    buttonSize === "sm" ? "px-3 py-2 text-sm" : "px-4 py-2 text-sm";

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row items-center gap-2">
        <button
          onClick={isRecording ? onStopRecording : onStartRecording}
          disabled={isTranscribing}
          className={`flex items-center gap-2 ${buttonClasses} rounded-lg font-medium transition-all hover-lift ${
            isRecording
              ? "bg-red-500 text-white hover:bg-red-600 breathing"
              : "bg-purple-600 text-white hover:bg-purple-700"
          } ${isTranscribing ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isRecording ? (
            <>
              <MicOff size={16} />
              <span>Stop Recording</span>
            </>
          ) : (
            <>
              <Mic size={16} />
              <span>{buttonText}</span>
            </>
          )}
        </button>

        {isTranscribing && (
          <div className="flex items-center gap-2 text-purple-600 animate-fadeIn">
            <Loader2 className="animate-spin" size={16} />
            <span className="text-xs font-medium">Transcribing...</span>
          </div>
        )}
      </div>

      {/* Voice Visualizer */}
      {isRecording && audioStream && (
        <div className="animate-fadeIn">
          <VoiceVisualizer isRecording={isRecording} stream={audioStream} />
        </div>
      )}
    </div>
  );
};

// Progress Ring Component - Optimized size
const ProgressRing = ({ progress, size = 32, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#7c3aed"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="progress-ring"
      />
    </svg>
  );
};

const App = () => {
  const [currentScreen, setCurrentScreen] = useState("welcome");
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentCategory, setCurrentCategory] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [savedStatus, setSavedStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [occupation, setOccupation] = useState("");
  const [currentChallenge, setCurrentChallenge] = useState("");
  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [isCheckingContext, setIsCheckingContext] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showContextSuccess, setShowContextSuccess] = useState(false);
  const [aiServiceError, setAiServiceError] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [lastCheckLength, setLastCheckLength] = useState(0);
  const [challengeCompleted, setChallengeCompleted] = useState(false);
  const MAX_FOLLOWUPS = 5;

  // Results state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);

  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const STORAGE_KEY = "questionnaire_answers";
  const REPORT_STORAGE_KEY = "questionnaire_report";

  // DEV: Results preview via URL flag (?resultsPreview=1)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPreview = params.get("resultsPreview") === "1";

    // Only allow in development
    if (
      isPreview &&
      (process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost")
    ) {
      const mock = [
        "## Your Purpose Profile Mini",
        "",
        `Hi ${firstName || "test"},`,
        "",
        "We're glad you made it. Based on your answers, here's a glimpse into the deeper patterns that might be shaping your life — especially as you navigate test.",
        "",
        "_Keep in mind: this is just a snapshot based on 4 questions. Your full Purpose Profile is based on 25 questions — revealing layers we can only hint at here._",
        "",
        "## What Drives You (Early Glimpse)",
        "",
        "Your responses reveal someone operating in protective mode — giving minimal information while still seeking understanding. This pattern of cautious engagement likely shows up everywhere in your life. You're simultaneously reaching for growth while keeping your cards close to your chest.",
        "",
        "This creates an interesting paradox: you want to be seen and understood, but you're not quite ready to be fully visible. Your current challenge is probably connected to this very pattern — situations where you need to show up more fully than feels comfortable, or where surface-level engagement isn't getting you the results you need.",
        "",
        "> To truly understand what drives you, what holds you back, and why you're facing this at this exact moment in your life, we need to uncover more layers through the full Purpose Profile.",
      ].join("\n");

      setGeneratedReport(mock);
      setReportGeneratedAt(Date.now());
      setIsGenerating(false);
      setGenerationError(null);
      setCurrentScreen("results");
    }
  }, [firstName]);

  // NEW: load Meta Pixel once and send PageView (Pixel + CAPI)
  useEffect(() => {
    loadMetaPixel();

    const { fbp, fbc } = getMetaBrowserIds();
    const eventID = genEventID();

    // Browser pixel PageView
    if (window.fbq) {
      window.fbq("track", "PageView", {}, { eventID });
    }

    // Server mirror (CAPI) for PageView
    fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventName: "PageView", fbp, fbc, eventID }),
    }).catch(() => {});
  }, []);

  // Initial loading animation
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Get email from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, []);

  // Update current category when question changes
  useEffect(() => {
    const categoryIndex = categories.findIndex(
      (cat) =>
        currentQuestion >= cat.startIndex && currentQuestion <= cat.endIndex
    );
    if (categoryIndex !== -1) {
      setCurrentCategory(categoryIndex);
    }
  }, [currentQuestion]);

  // Save to localStorage
  const saveToStorage = useCallback(
    (data) => {
      try {
        const storageData = {
          answers: data,
          firstName: firstName,
          email: email,
          dateOfBirth: dateOfBirth,
          occupation: occupation,
          currentChallenge: currentChallenge,
          currentScreen: currentScreen,
          currentQuestion: currentQuestion,
          challengeCompleted: challengeCompleted,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
        setSavedStatus("saved");
        setTimeout(() => setSavedStatus(""), 2000);
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    },
    [
      firstName,
      email,
      dateOfBirth,
      occupation,
      currentChallenge,
      currentScreen,
      currentQuestion,
      challengeCompleted,
    ]
  );

  // Save report to localStorage
  const saveReportToStorage = useCallback(
    (report, generatedAt) => {
      try {
        const reportData = {
          report: report,
          generatedAt: generatedAt,
          firstName: firstName,
          email: email,
        };
        localStorage.setItem(REPORT_STORAGE_KEY, JSON.stringify(reportData));
      } catch (error) {
        console.error("Error saving report to localStorage:", error);
      }
    },
    [firstName, email]
  );

  // Load from localStorage (including report)
  useEffect(() => {
    try {
      // Load questionnaire data
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);
        if (parsedData.answers && Object.keys(parsedData.answers).length > 0) {
          setAnswers(parsedData.answers);
          setFirstName(parsedData.firstName || "");
          setDateOfBirth(parsedData.dateOfBirth || "");
          setOccupation(parsedData.occupation || "");
          setCurrentChallenge(parsedData.currentChallenge || "");
          setChallengeCompleted(
            parsedData.challengeCompleted || !!parsedData.currentChallenge
          );
          if (!email && parsedData.email) {
            setEmail(parsedData.email);
          }
          setCurrentQuestion(parsedData.currentQuestion || 0);
        }
      }

      // Load report data if exists
      const savedReport = localStorage.getItem(REPORT_STORAGE_KEY);
      if (savedReport) {
        const reportData = JSON.parse(savedReport);
        if (reportData.report && reportData.generatedAt) {
          setGeneratedReport(reportData.report);
          setReportGeneratedAt(reportData.generatedAt);
          setCurrentScreen("results");
          // Update firstName and email from report if not already set
          if (!firstName && reportData.firstName) {
            setFirstName(reportData.firstName);
          }
          if (!email && reportData.email) {
            setEmail(reportData.email);
          }
        }
      } else if (saved) {
        // Only set screen from saved data if no report exists
        const parsedData = JSON.parse(saved);
        setCurrentScreen(parsedData.currentScreen || "welcome");
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
  }, []);

  // Auto-save
  useEffect(() => {
    if (
      Object.keys(answers).length > 0 ||
      firstName ||
      email ||
      dateOfBirth ||
      occupation ||
      currentChallenge
    ) {
      saveToStorage(answers);
    }
  }, [
    answers,
    firstName,
    email,
    dateOfBirth,
    occupation,
    currentChallenge,
    challengeCompleted,
    saveToStorage,
  ]);

  // Auto-save report when it changes
  useEffect(() => {
    if (generatedReport && reportGeneratedAt) {
      saveReportToStorage(generatedReport, reportGeneratedAt);
    }
  }, [generatedReport, reportGeneratedAt, saveReportToStorage]);

  // Auto-resize textarea - optimized for mobile
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 300) + "px";
    }
  }, [answers[currentQuestion]]);

  const checkContextAndGetFollowUp = async (questionId, answer) => {
    // Don't check for very short answers
    if (!answer || answer.trim().length < 30) {
      setShowFollowUp(false);
      setShowContextSuccess(false);
      return;
    }

    // Don't check if we're already checking
    if (isCheckingContext) return;

    // Check if answer has changed since last check (more than 10 characters difference)
    const currentLength = answer.trim().length;
    const lengthDifference = Math.abs(currentLength - lastCheckLength);

    // Only check if there's meaningful change or it's the first check
    if (lengthDifference < 10 && lastCheckLength > 0) {
      return;
    }

    setIsCheckingContext(true);
    setLastCheckLength(currentLength);

    try {
      let result = { needsMoreContext: false };

      if (
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost"
      ) {
        result = await checkContextDirectly(questionId, answer);
      } else {
        const response = await fetch("/api/check-context", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            questionId: questionId,
            question: questionsConfig[currentQuestion].question,
            answer: answer,
            previousAnswers: Object.entries(answers)
              .slice(0, currentQuestion)
              .map(([idx, ans]) => ({
                question: questionsConfig[idx]?.question,
                answer: ans,
              }))
              .filter((qa) => qa.answer && qa.answer.trim()),
            allQuestions: questionsConfig,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          result = data;

          // Check for AI service errors
          if (data.error) {
            console.error("AI service error:", data.error);
            setShowContextSuccess(false);
            setShowFollowUp(false);
            setFollowUpQuestion("");
            setAiServiceError(
              "Our AI assistant is temporarily unavailable. Your answers are still being saved."
            );
            // Clear error after 10 seconds
            setTimeout(() => setAiServiceError(null), 10000);
            return;
          }
        } else {
          console.error("Failed to check context:", response.status);
          return;
        }
      }

      // Update UI based on result
      if (result.needsMoreContext && result.followUpQuestion) {
        // Only show new follow-up if it's different from current one
        if (result.followUpQuestion !== followUpQuestion) {
          setFollowUpQuestion(result.followUpQuestion);
          setShowFollowUp(true);
          setShowContextSuccess(false);
          if (!showFollowUp) {
            // Only increment if showing a new follow-up
            setFollowUpCount((prev) => prev + 1);
          }
        }
      } else if (answer.trim().length > 50) {
        // Show success message
        setShowFollowUp(false);
        setFollowUpQuestion("");
        setShowContextSuccess(true);
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setShowContextSuccess(false);
        }, 5000);
      } else {
        // Not enough context but too short for follow-up
        setShowFollowUp(false);
        setShowContextSuccess(false);
      }
    } catch (error) {
      console.error("Error checking context:", error);
    } finally {
      setIsCheckingContext(false);
    }
  };

  // Enhanced checkContextDirectly function
  const checkContextDirectly = async (questionId, answer) => {
    if (!process.env.REACT_APP_OPENAI_API_KEY) {
      // Enhanced mock logic for development
      const wordCount = answer.split(" ").length;
      const hasExample =
        answer.toLowerCase().includes("for example") ||
        answer.toLowerCase().includes("once") ||
        answer.toLowerCase().includes("when i") ||
        answer.toLowerCase().includes("remember when") ||
        answer.toLowerCase().includes("specific");

      // More nuanced checking
      if (wordCount < 25 && !hasExample) {
        const currentQ = questionsConfig[currentQuestion];
        const categoryId = currentQ.categoryId;

        // Category-specific follow-ups for better testing
        const mockFollowUpsByCategory = {
          "hidden-signal": [
            "That's fascinating. Can you share a specific moment when you felt this way?",
            "I can sense this matters deeply to you. What does this reveal about who you are at your core?",
            "Thank you for sharing. What specific situation made this most clear to you?",
            "That's really insightful. How does this typically show up in your daily life?",
            "I appreciate your honesty. Can you think of a recent example where this played out?",
            "That resonates deeply. When did you first become aware of this pattern?",
          ],
        };

        const categoryFollowUps = mockFollowUpsByCategory[categoryId] || [
          "That's interesting. Could you share a specific example or moment?",
          "I'd love to understand more. What personal experience led you to this?",
        ];

        return {
          needsMoreContext: true,
          followUpQuestion:
            categoryFollowUps[questionId % categoryFollowUps.length],
        };
      }

      return { needsMoreContext: false };
    }

    // For production with OpenAI
    try {
      const allQuestionsContext = questionsConfig
        .map((q, idx) => `Q${idx + 1}: ${q.question}`)
        .join("\n");

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-3.5-turbo",
            messages: [
              {
                role: "system",
                content: `You are a deeply empathetic AI companion helping someone discover their purpose. Assess if their answer provides enough specific, personal context for the CURRENT question only.

Be aware of all 3 questions to avoid asking about topics covered elsewhere:
${allQuestionsContext}

Only probe deeper into the current topic. If more context needed, generate ONE warm, therapeutic follow-up (max 2 sentences) that asks for a specific example or feeling. 

Respond with JSON: { "needsMoreContext": boolean, "followUpQuestion": string or null }`,
              },
              {
                role: "user",
                content: `Question: "${questionsConfig[currentQuestion].question}"
Answer: "${answer}"

Does this provide enough personal context for THIS specific question?`,
              },
            ],
            temperature: 0.7,
            max_tokens: 150,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return JSON.parse(data.choices[0].message.content);
      }
    } catch (error) {
      console.error("Direct OpenAI call failed:", error);
    }

    return { needsMoreContext: false };
  };

  const handleInputChange = (value) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion]: value,
    }));
  };

  // Enhanced useEffect for continuous context checking with debouncing
  useEffect(() => {
    const currentAnswer = answers[currentQuestion] || "";

    // Clear any existing timeout
    const timeoutId = setTimeout(() => {
      // Always check when there's content, regardless of what was shown before
      if (currentAnswer.length > 30) {
        checkContextAndGetFollowUp(currentQuestion + 1, currentAnswer);
      } else {
        // Clear messages if answer is too short
        setShowFollowUp(false);
        setShowContextSuccess(false);
      }
    }, 2000); // Check after 2 seconds of no typing

    return () => clearTimeout(timeoutId);
  }, [answers[currentQuestion], currentQuestion]); // Re-run whenever answer changes

  const handleFollowUpSubmit = () => {
    setShowFollowUp(false);
    setFollowUpQuestion("");
    setLastCheckLength(0); // Reset check tracking
  };

  // Unified progress calculation including currentChallenge
  const isAnswered = (val) => typeof val === "string" && val.trim().length >= 3;
  const getUnifiedProgress = () => {
    const totalQuestions = questionsConfig.length + 1; // +1 for currentChallenge
    let completedQuestions = 0;
    
    // Count currentChallenge as completed if it has content
    if (isAnswered(currentChallenge)) {
      completedQuestions += 1;
    }
    
    // Count main questions completed
    completedQuestions += Object.keys(answers).filter((key) => isAnswered(answers[key])).length;
    
    return Math.round((completedQuestions / totalQuestions) * 100);
  };

  const progress = getUnifiedProgress();

  // Get progress for current category
  const getCategoryProgress = (category) => {
    const categoryQuestions = [];
    for (let i = category.startIndex; i <= category.endIndex; i++) {
      categoryQuestions.push(i);
    }
    const answeredInCategory = categoryQuestions.filter((q) =>
      answers[q]?.trim()
    ).length;
    return (answeredInCategory / categoryQuestions.length) * 100;
  };

  // Helper function to check if all questions are completed (including currentChallenge)
  const allQuestionsCompleted = () => {
    // Check currentChallenge is completed
    if (!isAnswered(currentChallenge)) {
      return false;
    }
    
    // Check all main questions are completed
    return questionsConfig.every(
      (_, index) => isAnswered(answers[index])
    );
  };

  const handleNext = () => {
    scrollToTop();
    if (currentQuestion < questionsConfig.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowFollowUp(false);
      setShowContextSuccess(false);
      setFollowUpQuestion("");
      setLastCheckLength(0); // Reset check tracking
    } else {
      // On last question, validate before proceeding to personal info
      if (allQuestionsCompleted()) {
        setCurrentScreen("personalInfo");
      } else {
        // Show validation error
        setSubmitStatus("Please complete all questions before proceeding.");
        // Clear the error after 3 seconds
        setTimeout(() => setSubmitStatus(""), 3000);
      }
    }
  };

  const handlePrevious = () => {
    scrollToTop();
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowFollowUp(false);
      setShowContextSuccess(false);
      setFollowUpQuestion("");
      setLastCheckLength(0); // Reset check tracking
    } else if (currentQuestion === 0) {
      // Navigate back to currentChallenge from first question
      setCurrentScreen("currentChallenge");
    }
  };

  const handleCategoryChange = (categoryIndex) => {
    scrollToTop();
    const category = categories[categoryIndex];
    setCurrentQuestion(category.startIndex);
    setCurrentCategory(categoryIndex);
    setShowFollowUp(false);
    setShowContextSuccess(false);
    setFollowUpQuestion("");
    setLastCheckLength(0); // Reset check tracking
    setShowCategoryDropdown(false);
  };

  const handleStartAssessment = () => {
    scrollToTop();
    // If challenge already completed, go straight to questions
    if (challengeCompleted && currentChallenge.trim()) {
      setCurrentScreen("questions");
    } else {
      setCurrentScreen("currentChallenge");
    }
  };

  const handleChallengeSubmit = () => {
    scrollToTop();
    if (!currentChallenge.trim()) {
      alert("Please describe your current challenge");
      return;
    }
    setChallengeCompleted(true);
    setCurrentScreen("questions");
  };

  const handleBackToAssessment = () => {
    scrollToTop();
    setCurrentScreen("personalInfo");
    setGenerationError(null);
  };

  const handleStartOver = () => {
    scrollToTop();
    setAnswers({});
    setFirstName("");
    setEmail("");
    setDateOfBirth("");
    setOccupation("");
    setCurrentChallenge("");
    setChallengeCompleted(false);
    setCurrentQuestion(0);
    setCurrentScreen("welcome");
    setGeneratedReport(null);
    setReportGeneratedAt(null);
    setGenerationError(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(REPORT_STORAGE_KEY);
  };

  const handlePersonalInfoSubmit = () => {
    if (!firstName.trim()) {
      alert("Please enter your first name");
      return;
    }
    if (!email.trim()) {
      alert("Email address is required");
      return;
    }
    if (!dateOfBirth.trim()) {
      alert("Please enter your date of birth");
      return;
    }
    if (!occupation.trim()) {
      alert("Please enter your occupation");
      return;
    }

    // Proceed to final submission
    handleSubmit();
  };

  // Voice recording functions
  const startRecording = async (targetField = "question") => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        await transcribeAudio(audioBlob, targetField);
        stream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Please allow microphone access to use voice input.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Use backend API for transcription
  const transcribeAudio = async (audioBlob, targetField = "question") => {
    setIsTranscribing(true);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result.split(",")[1]; // Remove data:audio/webm;base64, prefix
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);

      const audioData = await base64Promise;

      const response = await fetch("/api/transcribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          audioData: audioData,
          mimeType: "audio/webm",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const data = await response.json();

      if (!data.text) {
        throw new Error("Invalid response from transcription service");
      }

      // Update the appropriate field based on targetField
      if (targetField === "question") {
        setAnswers((prev) => ({
          ...prev,
          [currentQuestion]:
            (prev[currentQuestion] || "") +
            (prev[currentQuestion] ? " " : "") +
            data.text,
        }));
      } else if (targetField === "currentChallenge") {
        setCurrentChallenge((prev) => prev + (prev ? " " : "") + data.text);
      }

      setIsTranscribing(false);
    } catch (error) {
      console.error("Transcription error:", error);
      alert(`Error transcribing audio: ${error.message}`);
      setIsTranscribing(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus("");

    const answeredQuestions = Object.keys(answers).filter((key) =>
      answers[key]?.trim()
    );
    if (answeredQuestions.length < questionsConfig.length) {
      setSubmitStatus("Please answer all questions before submitting.");
      setIsSubmitting(false);
      return;
    }

    const { fbp, fbc } = getMetaBrowserIds();
    const leadEventID = genEventID();

    // Browser pixel Lead
    if (window.fbq) {
      window.fbq("track", "Lead", {}, { eventID: leadEventID });
    }

    // First, submit to Airtable for storage
    const airtableData = {
      timestamp: new Date().toISOString(),
      firstName,
      email,
      dateOfBirth,
      occupation,
      currentChallenge,
      responses: questionsConfig.map((q, index) => ({
        question_id: q.id,
        question: q.question,
        answer: answers[index] || "",
      })),
      fbp,
      fbc,
      eventID: leadEventID,
    };

    try {
      // Generate the report first
      setIsGenerating(true);
      setCurrentScreen("results"); // Show results screen with loading state

      const reportData = {
        firstName,
        email,
        dateOfBirth,
        occupation,
        currentChallenge,
        responses: questionsConfig.map((q, index) => ({
          question_id: q.id,
          question: q.question,
          answer: answers[index] || "",
        })),
      };

      const reportResponse = await fetch("/api/generate-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reportData),
      });

      if (!reportResponse.ok) {
        const errorData = await reportResponse.json();
        console.error("Report API error:", errorData);
        throw new Error(
          errorData.error || errorData.details || "Report generation failed"
        );
      }

      const reportResult = await reportResponse.json();
      console.log("Report API response:", reportResult);

      if (!reportResult.success || !reportResult.report) {
        console.error("Invalid report response:", reportResult);
        throw new Error("Invalid report response");
      }

      // Set the report generation timestamp
      const generatedAt = Date.now();

      // Now submit complete data to Airtable (including the generated report)
      const completeData = {
        ...airtableData,
        generatedReport: reportResult.report,
        generationTime: reportResult.generationTime,
        reportGeneratedAt: new Date(generatedAt).toISOString(),
      };

      const airtableResponse = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(completeData),
      });

      if (!airtableResponse.ok) {
        console.warn(
          "Airtable submission failed, but report was generated successfully"
        );
      }

      // Store report analytics
      try {
        await fetch("/api/store-report", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName,
            email,
            report: reportResult.report,
            generationTime: reportResult.generationTime,
            success: true,
          }),
        });
      } catch (error) {
        console.warn("Failed to store report analytics:", error);
      }

      setGeneratedReport(reportResult.report);
      setReportGeneratedAt(generatedAt);
      setGenerationError(null);
      setCurrentScreen("results");

      // Save report to localStorage
      saveReportToStorage(reportResult.report, generatedAt);

      // Clear questionnaire data from localStorage since report is generated
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Report generation error:", error);
      console.error("Error message:", error.message);
      setGenerationError(error.message);
      setCurrentScreen("results");
    } finally {
      setIsGenerating(false);
      setIsSubmitting(false);
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center animate-fadeIn">
          <div className="w-12 h-12 mx-auto mb-3">
            <Sparkles className="w-full h-full text-purple-600 animate-pulse" />
          </div>
          <h2 className="text-xl font-medium gradient-text">Loading...</h2>
        </div>
      </div>
    );
  }

  // Welcome Screen - Fully optimized with all feedback
  if (currentScreen === "welcome") {
    return (
      <div className="min-h-screen gradient-bg hero-gradient-bg py-6 px-4 page-transition">
        <div className="max-w-2xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8 animate-fadeIn">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-full text-purple-700 text-xs font-medium mb-6">
              <Sparkles size={14} />
              <span>Built for Corporate Professionals</span>
            </div>

            {/* Improved headline with emotional hook first */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">
                Feeling stuck in a corporate job{" "}
              </span>
              <span className="gradient-text">
                that no longer fulfills you?
              </span>
            </h1>

          </div>
          {/* Main Card */}
          <div className="glass rounded-2xl shadow-xl p-6 md:p-8 mb-6 card-hover relative overflow-hidden">
            <div className="text-center mb-6">
              <h3 className="text-xl sm:text-2xl font-medium text-gray-900 mb-2">
              Discover what kind of business or career path truly fits you AND how to start without giving up your paycheck.
              </h3>
              <p className="text-sm text-gray-600">
                How? Take this short, AI-guided test
              </p>
            </div>
        

            {/* Process List - Updated with feedback */}
            <div className="mb-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                  <span className="text-lg flex-shrink-0">⏱️</span>
                  <p className="text-sm font-medium text-gray-900">
                    Takes 3 minutes • You can answer by voice
                  </p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                  <span className="text-lg flex-shrink-0">🧠</span>
                  <p className="text-sm font-medium text-gray-900">
                    Smart AI reflections and tailored follow-up questions
                  </p>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                  <span className="text-lg flex-shrink-0">📋</span>
                  <p className="text-sm font-medium text-gray-900">
                    Instant mini report revealing your true calling
                  </p>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                  <span className="text-lg flex-shrink-0">🎁</span>
                  <p className="text-sm font-medium text-gray-900">
                    100% free • No credit card or account required
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section with hierarchy */}
            <div className="space-y-3">
              <button
                onClick={handleStartAssessment}
                className="w-full premium-button text-white rounded-xl py-3.5 px-6 font-semibold text-base hover-lift flex items-center justify-center gap-2"
              >
                <span>Start My Free Mini-Assessment</span>
                <ArrowRight size={18} />
              </button>

              {/* Show option to continue if already in progress
              {Object.keys(answers).filter((key) => answers[key]?.trim())
                .length > 0 && (
                <button
                  onClick={() => {
                    scrollToTop();
                    setCurrentScreen("questions");
                  }}
                  className="w-full bg-gray-100 text-gray-700 rounded-xl py-3 px-6 font-semibold text-sm hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                >
                  <span>Continue from Question {currentQuestion + 1}</span>
                  <ChevronRight size={16} />
                </button>
              )} */}
            </div>

            {/* Social Proof */}
            {/* <div className="text-center mt-4 text-s text-gray-600 font-bold">
              <CheckCircle size={14} className="inline mr-1 text-green-600" />
              4.9/5 from 100+ users who found their path
            </div> */}

            {/* Legal Links */}
            <p className="text-center text-[10px] text-gray-500 mt-3">
              By clicking "Start My Free Mini-Assessment" you agree with the{" "}
              <a
                href="https://mypurposeprofile.com/legal/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-800 underline"
              >
                Terms
              </a>
              ,{" "}
              <a
                href="https://mypurposeprofile.com/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-800 underline"
              >
                Privacy
              </a>
              , and{" "}
              <a
                href="https://mypurposeprofile.com/legal/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-800 underline"
              >
                Cookie Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Current Challenge Screen - Mobile optimized with consistent styling
  if (currentScreen === "currentChallenge") {
    return (
      <div className="min-h-screen gradient-bg py-4 px-4 page-transition">
        <div className="max-w-2xl mx-auto">
          {/* Header - Consistent with main questions */}
          <div className="mb-4 animate-fadeIn">
            <div className="text-center mb-3">
              <h1 className="text-lg sm:text-xl md:text-2xl font-medium text-gray-900">
                Hi{firstName ? ` ${firstName}` : ""}, let's discover your purpose
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Your progress is saved automatically
              </p>
            </div>

            {/* Overall Progress - Compact */}
            <div className="glass rounded-xl p-3 subtle-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ProgressRing progress={progress} />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">
                      {progress}% Complete
                    </p>
                    <p className="text-[10px] text-gray-600">
                      {(() => {
                        let completed = 0;
                        if (currentChallenge?.trim()) completed += 1;
                        completed += Object.keys(answers).filter((key) => answers[key]?.trim()).length;
                        return `${completed} of ${questionsConfig.length + 1} answered`;
                      })()}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-gray-600 text-xs animate-fadeIn">
                    <Clock size={12} />
                    <span>Auto-saved</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question Card - Consistent styling with main questions */}
          <div className="glass rounded-xl shadow-lg p-4 sm:p-6 mb-4 animate-slideUp">
            {/* Question Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-purple-600">
                  Question 1 of {questionsConfig.length + 1}
                </p>

                {/* Question Navigation Pills - 1-based display; 1 = currentChallenge */}
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      scrollToTop();
                      setCurrentScreen("currentChallenge");
                    }}
                    className="w-7 h-7 rounded-md text-[10px] font-semibold transition-all bg-purple-600 text-white shadow-md"
                  >
                    1
                  </button>
                  {questionsConfig.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        scrollToTop();
                        setCurrentScreen("questions");
                        setCurrentQuestion(index);
                        setShowFollowUp(false);
                        setShowContextSuccess(false);
                        setFollowUpQuestion("");
                        setLastCheckLength(0);
                        setAiServiceError(null);
                      }}
                      className={`w-7 h-7 rounded-md text-[10px] font-semibold transition-all ${
                        (index === 0 && isAnswered(currentChallenge)) ||
                        (index > 0 && isAnswered(answers[index - 1]))
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {index + 2}
                    </button>
                  ))}
                </div>
              </div>

              {/* Question subtitle */}
              <p className="text-xs font-semibold text-gray-700 mb-2">
                Let's understand your situation.
              </p>

              {/* Main question */}
              <h2 className="text-base sm:text-lg md:text-xl font-medium text-gray-900 leading-relaxed">
                What brings you here today?
              </h2>

              {/* Question description/guidance */}
              <p className="text-sm text-gray-600 mt-2">
                Understanding your current career situation helps us provide more relevant insights
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <div className="relative">
                  <textarea
                    value={currentChallenge}
                    onChange={(e) => setCurrentChallenge(e.target.value)}
                    placeholder="Describe your current career situation and the challenge you're facing.."
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-purple-500 transition-all text-sm resize-none"
                    rows={4}
                    autoFocus
                  />
                </div>

                {/* Voice Recording for Current Challenge */}
                <div className="mt-3">
                  <VoiceRecordingButton
                    isRecording={isRecording}
                    isTranscribing={isTranscribing}
                    audioStream={audioStream}
                    onStartRecording={() => startRecording("currentChallenge")}
                    onStopRecording={stopRecording}
                    buttonText="Record Answer"
                    buttonSize="sm"
                  />
                </div>

                <p className="text-[10px] xs:text-center md:text-left text-gray-500 mt-1.5">
                  The more input, the richer the output!
                </p>
              </div>
            </div>

            {/* Navigation buttons - Consistent with main questions */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() => {
                  scrollToTop();
                  setCurrentScreen("welcome");
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-all font-medium"
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>

              <button
                onClick={handleChallengeSubmit}
                disabled={!currentChallenge.trim()}
                className={`flex items-center gap-1 px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium hover-lift ${
                  !currentChallenge.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Personal Info Screen - Mobile optimized
  if (currentScreen === "personalInfo") {
    return (
      <div className="min-h-screen gradient-bg py-6 px-4 page-transition">
        <div className="max-w-xl mx-auto">
          <div className="glass rounded-2xl shadow-xl p-6 md:p-8">
            {/* Back Navigation */}
            <div className="mb-4">
              <button
                onClick={() => {
                  scrollToTop();
                  setCurrentScreen("questions");
                }}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium"
              >
                <ChevronLeft size={16} />
                <span>Back</span>
              </button>
            </div>

            {/* Welcome Message */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-medium text-gray-900 mb-2">
                Great job! Final step
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                We need your details to send your results
              </p>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                <CheckCircle size={12} />
                <span>4/4 questions completed</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-purple-500 transition-all text-sm"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@domain.com"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-purple-500 transition-all text-sm pl-9"
                    autoComplete="email"
                    inputMode="email"
                  />
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-purple-500 transition-all text-sm"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Current Occupation
                </label>
                <input
                  type="text"
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                  placeholder="e.g., Marketing Executive within Technology industry"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-purple-500 transition-all text-sm"
                />
              </div>
            </div>

            <button
              onClick={handlePersonalInfoSubmit}
              disabled={
                !firstName.trim() ||
                !email.trim() ||
                !dateOfBirth.trim() ||
                !occupation.trim() ||
                isSubmitting
              }
              className="w-full mt-6 premium-button text-white rounded-lg py-3 px-6 font-semibold text-sm hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  <span>Generating Your Report...</span>
                </>
              ) : (
                <>
                  <span>Get My Mini Report</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (currentScreen === "results") {
    return (
      <Results
        report={generatedReport}
        firstName={firstName}
        onBack={handleBackToAssessment}
        onStartOver={handleStartOver}
        isGenerating={isGenerating}
        generationError={generationError}
        reportGeneratedAt={reportGeneratedAt}
      />
    );
  }

  // Success Screen (DEV preview only)
  if (currentScreen === "success") {
    return (
      <SuccessPage
        sessionStatus="complete"
        purchaseValue={47.0}
        purchaseCurrency="USD"
      />
    );
  }

  // Questions Screen - Mobile optimized with updated questions text
  return (
    <div className="min-h-screen gradient-bg py-4 px-4 page-transition">
      <div className="max-w-2xl mx-auto">
        {/* Header - Compact for mobile */}
        <div className="mb-4 animate-fadeIn">
          <div className="text-center mb-3">
            <h1 className="text-lg sm:text-xl md:text-2xl font-medium text-gray-900">
              Hi{firstName ? ` ${firstName}` : ""}, let's discover your purpose
            </h1>
            <p className="text-xs sm:text-sm text-gray-600">
              Your progress is saved automatically
            </p>
          </div>

          {/* Overall Progress - Compact */}
          <div className="glass rounded-xl p-3 subtle-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ProgressRing progress={parseInt(progress)} />
                <div>
                  <p className="text-xs font-semibold text-gray-900">
                    {progress}% Complete
                  </p>
                  <p className="text-[10px] text-gray-600">
                    {(() => {
                      let completed = 0;
                      if (currentChallenge?.trim()) completed += 1;
                      completed += Object.keys(answers).filter((key) => answers[key]?.trim()).length;
                      return `${completed} of ${questionsConfig.length + 1} answered`;
                    })()}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                {savedStatus && (
                  <div className="flex items-center gap-1 text-green-600 text-xs animate-fadeIn">
                    <CheckCircle size={12} />
                    <span>Saved</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Question Card - Compact for mobile */}
        <div className="glass rounded-xl shadow-lg p-4 sm:p-6 mb-4 animate-slideUp">
          {/* Question Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium text-purple-600">
                Question {currentQuestion + 2} of {questionsConfig.length + 1}
              </p>

              {/* Question Navigation Pills - 1-based display; 1 = currentChallenge */}
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    scrollToTop();
                    setCurrentScreen("currentChallenge");
                  }}
                  className={`w-7 h-7 rounded-md text-[10px] font-semibold transition-all ${
                    isAnswered(currentChallenge)
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  1
                </button>
                {questionsConfig.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      scrollToTop();
                      setCurrentQuestion(index);
                      setShowFollowUp(false);
                      setShowContextSuccess(false);
                      setFollowUpQuestion("");
                      setLastCheckLength(0);
                      setAiServiceError(null);
                    }}
                    className={`w-7 h-7 rounded-md text-[10px] font-semibold transition-all ${
                      currentQuestion === index
                        ? "bg-purple-600 text-white shadow-md"
                        : isAnswered(answers[index])
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {index + 2}
                  </button>
                ))}
              </div>
            </div>

            {/* Question subtitle */}
            {questionsConfig[currentQuestion].subtitleAbove && (
              <p className="text-xs font-semibold text-gray-700 mb-2">
                {questionsConfig[currentQuestion].subtitleAbove}
              </p>
            )}

            {/* Main question */}
            <h2 className="text-base sm:text-lg md:text-xl font-medium text-gray-900 leading-relaxed">
              {questionsConfig[currentQuestion].question}
            </h2>

            {/* Question description/guidance */}
            {questionsConfig[currentQuestion].descriptionBelow && (
              <p className="text-xs text-gray-600 mt-2 italic">
                ({questionsConfig[currentQuestion].descriptionBelow})
              </p>
            )}
          </div>

          <div className="border-t border-gray-200 mb-4"></div>

          {/* Answer Input - Compact */}
          <div className="mb-4">
            <textarea
              ref={textareaRef}
              value={answers[currentQuestion] || ""}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Share your thoughts here... You can type or use voice recording."
              className={`w-full min-h-[120px] p-3 border-2 rounded-lg focus:border-purple-500 resize-none transition-all text-sm text-gray-800 leading-relaxed placeholder-gray-400 ${
                isCheckingContext ? "analyzing-border" : "border-gray-200"
              }`}
            />

            {/* AI Analyzing Indicator */}
            {isCheckingContext && (
              <div className="flex items-center gap-1.5 text-purple-600 text-xs mt-1.5 animate-fadeIn opacity-70">
                <div className="relative">
                  <Sparkles className="w-3 h-3" />
                  <div className="absolute inset-0 animate-ping">
                    <Sparkles className="w-3 h-3 opacity-30" />
                  </div>
                </div>
                <span className="text-[10px]">Listening carefully...</span>
              </div>
            )}
          </div>

          {/* AI Follow-up - Compact */}
          {showFollowUp && followUpQuestion && (
            <div className="mb-4 animate-fadeIn">
              <div className="p-3 glass-light rounded-lg border border-purple-100 shadow-sm">
                <div className="flex items-start gap-2">
                  <div className="p-1.5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-sm flex-shrink-0">
                    <Sparkles size={14} className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <p className="text-xs font-semibold text-purple-900">
                        Going Deeper
                      </p>
                      <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">
                        Optional
                      </span>
                    </div>
                    <p className="text-xs text-purple-800 leading-relaxed">
                      {followUpQuestion}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Context Success Message - Compact */}
          {showContextSuccess && !showFollowUp && (
            <div className="mb-4 animate-fadeIn">
              <div className="p-3 glass-light rounded-lg border border-green-100 shadow-sm bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-start gap-2">
                  <div className="p-1.5 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg shadow-sm flex-shrink-0">
                    <CheckCircle size={14} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <p className="text-xs font-semibold text-green-900">
                        Great Detail!
                      </p>
                      <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                        ✓ Complete
                      </span>
                    </div>
                    <p className="text-xs text-green-800 leading-relaxed">
                      Thank you for sharing such thoughtful context.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Voice Recording */}
          <VoiceRecordingButton
            isRecording={isRecording}
            isTranscribing={isTranscribing}
            audioStream={audioStream}
            onStartRecording={() => startRecording("question")}
            onStopRecording={stopRecording}
            buttonText="Record Answer"
            buttonSize="md"
          />

          {/* Navigation - Compact */}
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={handlePrevious}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-all font-medium"
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </button>

            <button
              onClick={handleNext}
              disabled={
                currentQuestion === questionsConfig.length - 1 &&
                !allQuestionsCompleted()
              }
              className={`flex items-center gap-1 px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium hover-lift ${
                currentQuestion === questionsConfig.length - 1 &&
                !allQuestionsCompleted()
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <span>
                {currentQuestion === questionsConfig.length - 1
                  ? allQuestionsCompleted()
                    ? "Continue"
                    : "Complete All"
                  : "Next"}
              </span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
